import React, { useEffect, useState } from "react";
import format from "date-fns/format";
import {
  Button,
  Header,
  Icon,
  Label,
  Message,
  Segment,
  Table,
} from "semantic-ui-react";
import firebase from "../../../app/config/firebase";
import { uploadSignedDocument } from "../leadActions";

const firestore = firebase.firestore();

function parseDate(value) {
  if (!value) return null;
  if (value.toDate) return value.toDate();

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatDate(value) {
  const date = parseDate(value);
  return date ? format(date, "d MMM yyyy HH:mm") : "-";
}

function sortDate(document) {
  const date = parseDate(
    document.signed_at || document.synced_at || document.created_at
  );
  return date ? date.getTime() : 0;
}

function documentTitle(document) {
  if (!document.document_type) return "Signed document";

  const documentType = document.document_type.toString().toLowerCase();

  if (documentType === "booking" || documentType === "booking_confirmation") {
    return "Booking";
  }

  if (documentType === "terms" || documentType === "terms_and_conditions") {
    return "Terms";
  }

  return document.document_type
    .replace(/[_-]/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function bookingReference(document) {
  return (
    document.booking_ref ||
    document.booking_reference ||
    document.booking_number ||
    document.booking_id ||
    "-"
  );
}

function todayInputValue() {
  const date = new Date();
  date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
  return date.toISOString().slice(0, 10);
}

function bookingUploadLabel(booking) {
  return (
    booking.booking_ref ||
    booking.booking_reference ||
    booking.booking_number ||
    booking.l_nr ||
    booking.id ||
    "Booking"
  );
}

function fallbackDocumentFromLead(lead) {
  if (!lead?.has_signed_documents && !lead?.last_signed_document_url) {
    return null;
  }

  return {
    id: "latest-signed-document",
    document_type: lead.last_signed_document_type,
    client_name: lead.last_signed_document_client_name,
    client_email: lead.last_signed_document_client_email,
    booking_id: lead.last_signed_document_booking_id,
    signed_at: lead.last_signed_document_at,
    signed_pdf_url: lead.last_signed_document_url,
    signed_pdf_hash: lead.last_signed_document_hash,
    status: lead.signed_document_status || "signed",
  };
}

export default function LeadDetailedSignedDocuments({ lead, currentUserProfile }) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadValues, setUploadValues] = useState({
    bookingId: "",
    documentType: "booking",
    signedAt: todayInputValue(),
  });

  useEffect(() => {
    if (!lead?.id) return undefined;

    setLoading(true);

    const unsubscribe = firestore
      .collection("leads")
      .doc(lead.id)
      .collection("signed_documents")
      .onSnapshot(
        (snapshot) => {
          const signedDocuments = [];

          snapshot.forEach((document) => {
            signedDocuments.push({ ...document.data(), id: document.id });
          });

          signedDocuments.sort((a, b) => sortDate(b) - sortDate(a));

          setDocuments(signedDocuments);
          setLoading(false);
        },
        (error) => {
          console.log("Signed documents listener error", error);
          setLoading(false);
        }
      );

    return () => unsubscribe();
  }, [lead?.id]);

  useEffect(() => {
    if (!lead?.id) return undefined;

    const unsubscribe = firestore
      .collection("bookings")
      .where("leadId", "==", lead.id)
      .onSnapshot(
        (snapshot) => {
          const leadBookings = [];

          snapshot.forEach((booking) => {
            leadBookings.push({ ...booking.data(), id: booking.id });
          });

          leadBookings.sort((a, b) => bookingUploadLabel(a).localeCompare(bookingUploadLabel(b)));
          setBookings(leadBookings);
        },
        (error) => {
          console.log("Bookings listener error", error);
        }
      );

    return () => unsubscribe();
  }, [lead?.id]);

  async function handleSignedDocumentUpload(event) {
    event.preventDefault();

    if (!lead?.id) return;

    const selectedBooking = bookings.find((booking) => booking.id === uploadValues.bookingId);

    try {
      setUploading(true);
      await uploadSignedDocument({
        leadId: lead.id,
        uploadFile,
        bookingId: uploadValues.bookingId,
        bookingRef: selectedBooking ? bookingUploadLabel(selectedBooking) : uploadValues.bookingId,
        clientName: lead.client_name || "",
        clientEmail: lead.client_email || "",
        documentType: uploadValues.documentType,
        signedAt: uploadValues.signedAt,
        displayName: currentUserProfile?.displayName || "",
        userUid: currentUserProfile?.id || "",
      });

      setUploadFile(null);
      setUploadValues({
        bookingId: "",
        documentType: "booking",
        signedAt: todayInputValue(),
      });
    } catch (error) {
      console.log(error?.message || error);
    } finally {
      setUploading(false);
    }
  }

  const fallbackDocument = fallbackDocumentFromLead(lead);
  const rows = documents.length > 0 ? documents : fallbackDocument ? [fallbackDocument] : [];
  const hasSignedDocuments = lead?.has_signed_documents === true || rows.length > 0;

  return (
    <div style={{ marginBottom: 20 }}>
      <Segment attached="top">
        <Header as="h3">
          <Icon name="file pdf outline" />
          <Header.Content>
            Signed Documents
            {hasSignedDocuments && (
              <Label color="green" style={{ marginLeft: 10 }}>
                Signed
              </Label>
            )}
          </Header.Content>
        </Header>
      </Segment>
      <Segment attached>
        {loading && <p>Loading signed documents...</p>}

        {!loading && rows.length === 0 && (
          <Message info>No signed documents recorded yet.</Message>
        )}

        {rows.length > 0 && (
          <div style={{ maxHeight: 300, overflowY: "auto", overflowX: "auto" }}>
          <Table celled compact="very" size="small" style={{ marginBottom: 0 }}>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Document</Table.HeaderCell>
                <Table.HeaderCell>Booking ID</Table.HeaderCell>
                <Table.HeaderCell>Signed</Table.HeaderCell>
                <Table.HeaderCell>Status</Table.HeaderCell>
                <Table.HeaderCell textAlign="right">PDF</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {rows.map((document) => (
                <Table.Row key={document.id}>
                  <Table.Cell>{documentTitle(document)}</Table.Cell>
                  <Table.Cell style={{ maxWidth: 150, wordBreak: "break-all" }}>
                    {bookingReference(document)}
                  </Table.Cell>
                  <Table.Cell>{formatDate(document.signed_at)}</Table.Cell>
                  <Table.Cell>
                    <Label color="green">
                      {document.status || "signed"}
                    </Label>
                  </Table.Cell>
                  <Table.Cell textAlign="right">
                    {document.signed_pdf_url ? (
                      <Button
                        as="a"
                        basic
                        color="orange"
                        compact
                        href={document.signed_pdf_url}
                        rel="noopener noreferrer"
                        size="small"
                        target="_blank"
                      >
                        Open PDF
                      </Button>
                    ) : (
                      "-"
                    )}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
          </div>
        )}

        <div style={{ marginTop: 20, paddingTop: 20, borderTop: "1px solid #eee" }}>
          <Header as="h4">Upload Signed PDF</Header>
          <form className="ui form" onSubmit={handleSignedDocumentUpload}>
            <div className="fields">
              <div className="six wide field">
                <label>Document Type</label>
                <select
                  value={uploadValues.documentType}
                  onChange={(event) => setUploadValues({ ...uploadValues, documentType: event.target.value })}
                >
                  <option value="booking">Booking</option>
                  <option value="terms">Terms</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="ten wide field">
                <label>Booking</label>
                <select
                  value={uploadValues.bookingId}
                  onChange={(event) => setUploadValues({ ...uploadValues, bookingId: event.target.value })}
                >
                  <option value="">No booking selected</option>
                  {bookings.map((booking) => (
                    <option key={booking.id} value={booking.id}>
                      {bookingUploadLabel(booking)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="fields">
              <div className="six wide field">
                <label>Signed Date</label>
                <input
                  type="date"
                  value={uploadValues.signedAt}
                  onChange={(event) => setUploadValues({ ...uploadValues, signedAt: event.target.value })}
                />
              </div>

              <div className="ten wide field">
                <label>PDF</label>
                <input
                  type="file"
                  accept="application/pdf"
                  style={{ width: "100%" }}
                  onChange={(event) => setUploadFile(event.currentTarget.files[0])}
                />
              </div>
            </div>

            <Button
              color="orange"
              compact
              disabled={uploading}
              loading={uploading}
              type="submit"
            >
              Upload Signed PDF
            </Button>
          </form>
        </div>

      </Segment>
    </div>
  );
}
